import { useSyncExternalStore } from 'react';
import { breakpoints } from '@/theme';

type MediaQueryEntry = {
  subscribe: (onStoreChange: () => void) => () => void;
  getSnapshot: () => boolean;
};

/**
 * One entry per distinct query, shared by every component watching it.
 *
 * These hooks run per *event*, not per page: useVibe calls useIsMobileBreakpoint and
 * ~30 components call useVibe, several once per row. Opening a MediaQueryList and
 * attaching a `resize` listener inside each call's effect meant a 580-card list
 * attached 580 listeners for one breakpoint, re-attaching on every change (the effect
 * depended on the matched value). One subscription per query makes that a handful for
 * the whole app.
 *
 * Filled lazily, since only the call sites know which queries exist.
 */
const entries = new Map<string, MediaQueryEntry>();

const createEntry = (query: string): MediaQueryEntry => {
  const list = window.matchMedia(query);
  const subscribers = new Set<() => void>();
  const notify = () => subscribers.forEach((onStoreChange) => onStoreChange());

  return {
    subscribe: (onStoreChange) => {
      subscribers.add(onStoreChange);
      // First subscriber attaches, last one detaches, so the listener exists only
      // while something is watching.
      if (subscribers.size === 1) {
        list.addEventListener('change', notify);
      }

      return () => {
        subscribers.delete(onStoreChange);
        if (subscribers.size === 0) {
          list.removeEventListener('change', notify);
        }
      };
    },
    getSnapshot: () => list.matches,
  };
};

const getEntry = (query: string): MediaQueryEntry => {
  const existing = entries.get(query);
  if (existing) {
    return existing;
  }

  const entry = createEntry(query);
  entries.set(query, entry);

  return entry;
};

// The MediaQueryList's own `change` event, not window `resize`: it fires when the match
// flips rather than continuously through a drag, and it covers queries resize never
// reported at all — useIsMobile watches pointer/hover, which no resizing raises.
//
// subscribe/getSnapshot come from the cached entry, so useSyncExternalStore sees stable
// identities and never resubscribes on its own. It re-reads the snapshot after
// subscribing, which is what the old effect's manual catch-up did by hand.
const useMediaQuery = (query: string): boolean => {
  const { subscribe, getSnapshot } = getEntry(query);

  return useSyncExternalStore(subscribe, getSnapshot);
};

export const useIsBreakpoint = (breakpoint: keyof typeof breakpoints) => {
  // Intentionally not Chakra's `useBreakpointValue` / `useMediaQuery` — they are not
  // performant and trigger too many re-renders.
  return useMediaQuery(`(max-width: ${breakpoints[breakpoint]})`);
};

export const useIsAtLeastBreakpoint = (
  breakpoint: keyof typeof breakpoints
) => {
  return useMediaQuery(`(min-width: ${breakpoints[breakpoint]})`);
};

export const useIsMobileBreakpoint = () => {
  return useIsBreakpoint('md');
};

/**
 * The planner PlatformBar only fits all of its controls at ~1100px and up.
 * Below that we condense the ViewPicker and DateRangePicker to reclaim
 * horizontal space (distinct from the mobile treatment at `md`).
 */
export const useIsCompactPlatformBar = () => {
  return useMediaQuery('(max-width: 1100px)');
};

/**
 * The customize tab rail doesn't wrap, and its widest set — All, five tabs, the
 * experience title, and Done — needs ~1000px. Below that we drop the "Customizing"
 * status, the title, and the All label to keep Done on screen. Customize mode is
 * already off below `md`, so this only spans the 769–1000px band.
 */
export const useIsCompactCustomizeBar = () => {
  return useMediaQuery('(max-width: 1000px)');
};

export const useIsLargeScreenOrSmaller = () => {
  return useIsBreakpoint('lg');
};

export const useIsShortScreen = () => {
  return useMediaQuery('(max-height: 800px)');
};

export const useIsTallScreen = () => {
  return useMediaQuery('(min-height: 1200px)');
};

/** Returns `true` is the device is most likely a mobile device */
export const useIsMobile = () => {
  const isMobile = useMediaQuery('(pointer: coarse) and (hover: none)');
  return { isMobile };
};
